import { Children, isValidElement, ReactElement, ReactNode } from 'react';

import TabPanel from './TabPanel';
import { ResolvedTabPanel, TabPanelProps } from './types';

const isTabPanelElement = (
  child: ReactElement,
): child is ReactElement<TabPanelProps> =>
  child.type === TabPanel || child.props?.label != null;

const readPanelProps = (child: ReactElement<TabPanelProps>): TabPanelProps =>
  child.props;

/** Normalizes `<TabPanel>` and legacy `<div label="…">` children into tab metadata. */
export const resolveTabPanels = (children: ReactNode): ResolvedTabPanel[] =>
  Children.toArray(children)
    .filter(Boolean)
    .filter(isValidElement)
    .filter(isTabPanelElement)
    .map((child, orderIndex) => {
      const {
        label,
        children: panelChildren,
        index: explicitIndex,
        withSearch,
        withFilter,
        withDocumentSearch,
        iconLabel,
        grow,
        tabId,
      } = readPanelProps(child);

      const index = explicitIndex ?? orderIndex;

      return {
        key: `${label}-${index}`,
        label,
        index,
        children: panelChildren,
        withSearch,
        withFilter,
        withDocumentSearch,
        iconLabel,
        grow,
        tabId,
      };
    });

export const findPanelByIndex = (
  panels: ResolvedTabPanel[],
  activeIndex: number,
): ResolvedTabPanel | undefined =>
  panels.find((panel) => panel.index === activeIndex) ?? panels[activeIndex];

export const resolveTabsVariant = ({
  variant,
  withBottomIndicator,
  isSidebar,
}: {
  variant?: 'legacy' | 'navigation' | 'sidebar';
  withBottomIndicator?: boolean;
  isSidebar?: boolean;
}) => {
  if (variant) {
    return variant;
  }
  if (withBottomIndicator) {
    return 'navigation' as const;
  }
  if (isSidebar) {
    return 'sidebar' as const;
  }
  return 'legacy' as const;
};
